import { type FC, useMemo } from "react";
import { Pressable, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../theme";
import { Text } from "../Text";
import {
  getMonthMatrix,
  getWeekdayLabels,
  isDateDisabled,
  isInRange,
  isSameDay,
  isSameMonth,
  type WeekStartsOn,
} from "./date-utils";

interface CalendarMonthProps {
  /** Any date within the month to render. */
  readonly month: Date;
  readonly weekStartsOn: WeekStartsOn;
  /** Reference "today" for the today marker. */
  readonly today: Date;
  /** Start of the current selection (single selection uses this alone). */
  readonly selectedStart: Date | null;
  /** End of the current selection (range mode only). */
  readonly selectedEnd: Date | null;
  /** Pending end used for range hover preview on web. */
  readonly hoveredDate: Date | null;
  readonly rangeMode: boolean;
  readonly minDate?: Date | undefined;
  readonly maxDate?: Date | undefined;
  readonly onSelectDate: (date: Date) => void;
  readonly onHoverDate?: ((date: Date | null) => void) | undefined;
  /** Override the default 40px cell size for narrow viewports. */
  readonly cellSize?: number | undefined;
}

const CalendarMonth: FC<CalendarMonthProps> = ({
  month,
  weekStartsOn,
  today,
  selectedStart,
  selectedEnd,
  hoveredDate,
  rangeMode,
  minDate,
  maxDate,
  onSelectDate,
  onHoverDate,
  cellSize: cellSizeProp,
}) => {
  const { theme } = useUnistyles();
  const weeks = useMemo(() => getMonthMatrix(month, weekStartsOn), [month, weekStartsOn]);
  const weekdayLabels = useMemo(() => getWeekdayLabels(weekStartsOn), [weekStartsOn]);

  const cell = cellSizeProp ?? DEFAULT_CELL;
  const circle = Math.round(cell * (DEFAULT_CIRCLE / DEFAULT_CELL));

  // While selecting a range with only a start chosen, treat the hovered/pending
  // day as the provisional end so the band and endpoints preview correctly.
  const effectiveEnd = selectedEnd ?? (rangeMode && selectedStart ? hoveredDate : null);
  const rangeBand = theme.currentTheme === "dark" ? theme.colors.blue[900] : theme.colors.blue[50];
  const primary = theme.colors.interactive.buttons.default_background;

  return (
    // `onPointerLeave` fires only when the pointer leaves the whole grid — not
    // when moving between day cells — so the range hover preview clears once
    // the cursor exits the calendar, without inter-cell flicker.
    <View
      style={[styles.month, { width: cell * 7 }]}
      onPointerLeave={onHoverDate ? () => onHoverDate(null) : undefined}
    >
      <View style={styles.weekRow}>
        {weekdayLabels.map((label, i) => (
          <View key={`wd-${i.toString()}`} style={[styles.cell, { width: cell, height: cell }]}>
            <Text textStyle="xxs/semibold" color="textMuted">
              {label}
            </Text>
          </View>
        ))}
      </View>

      {weeks.map((week, wi) => (
        <View key={`w-${wi.toString()}`} style={styles.weekRow}>
          {week.map((day) => {
            const disabled = isDateDisabled(day, minDate, maxDate);
            const outsideMonth = !isSameMonth(day, month);
            const isStart = selectedStart != null && isSameDay(day, selectedStart);
            const isEnd = effectiveEnd != null && isSameDay(day, effectiveEnd);
            const isEndpoint = isStart || isEnd;
            const inBand = rangeMode && isInRange(day, selectedStart, effectiveEnd);
            const isToday = isSameDay(day, today);

            return (
              <Pressable
                key={day.toISOString()}
                style={[styles.cell, { width: cell, height: cell }]}
                disabled={disabled}
                onPress={() => onSelectDate(day)}
                onHoverIn={onHoverDate ? () => onHoverDate(day) : undefined}
                accessibilityRole="button"
                accessibilityState={{ disabled, selected: isEndpoint }}
              >
                {inBand && <View style={[styles.band, { backgroundColor: rangeBand }]} pointerEvents="none" />}
                <View
                  style={[
                    styles.dayCircleBase,
                    { width: circle, height: circle, borderRadius: circle / 2 },
                    isToday && !isEndpoint && { borderWidth: 1, borderColor: primary },
                    isEndpoint && { backgroundColor: primary },
                  ]}
                >
                  <Text
                    textStyle={isEndpoint ? "sm/semibold" : "sm/regular"}
                    style={{
                      color:
                        isEndpoint ? theme.colors.white.default
                        : disabled || outsideMonth ? theme.colors.textMuted
                        : theme.colors.text,
                      opacity: disabled ? 0.4 : 1,
                    }}
                  >
                    {day.getDate()}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      ))}
    </View>
  );
};

CalendarMonth.displayName = "CalendarMonth";

export { CalendarMonth };

const DEFAULT_CELL = 40;
const DEFAULT_CIRCLE = 34;

const styles = StyleSheet.create(() => ({
  month: {},
  weekRow: {
    flexDirection: "row",
  },
  cell: {
    alignItems: "center",
    justifyContent: "center",
  },
  band: {
    ...StyleSheet.absoluteFillObject,
  },
  dayCircleBase: {
    alignItems: "center",
    justifyContent: "center",
  },
}));
